/**
 * Critic service API calls — personas CRUD and async critique jobs.
 *
 * Critique jobs are async: POST returns 202 with a job_id; callers must
 * poll GET /v1/critique/{job_id} until status is 'completed' | 'failed'.
 */

import { criticApi } from './client';
import type {
  ApiResponse,
  PersonaList,
  PersonaOut,
  CritiqueJobCreated,
  CritiqueJobOut,
  CritiqueContent,
} from '../types';

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

export async function listPersonas(
  limit = 50,
  offset = 0,
): Promise<ApiResponse<PersonaList>> {
  return criticApi.get(`/v1/personas?limit=${limit}&offset=${offset}`);
}

export async function createPersona(body: {
  name: string;
  description?: string | null;
  system_prompt: string;
  focus_areas?: string[];
  model?: string | null;
  max_top_items?: number;
}): Promise<ApiResponse<PersonaOut>> {
  return criticApi.post('/v1/personas', body);
}

export async function updatePersona(
  personaId: string,
  body: {
    name?: string;
    description?: string | null;
    system_prompt?: string;
    focus_areas?: string[];
    model?: string | null;
    max_top_items?: number;
  },
): Promise<ApiResponse<PersonaOut>> {
  return criticApi.patch(`/v1/personas/${personaId}`, body);
}

export async function deletePersona(personaId: string): Promise<void> {
  return criticApi.delete(`/v1/personas/${personaId}`);
}

// ---------------------------------------------------------------------------
// Critique jobs
// ---------------------------------------------------------------------------

export async function submitCritique(body: {
  persona_id: string;
  content: CritiqueContent;
}): Promise<ApiResponse<CritiqueJobCreated>> {
  return criticApi.post('/v1/critique', body);
}

export async function getCritique(
  jobId: string,
): Promise<ApiResponse<CritiqueJobOut>> {
  return criticApi.get(`/v1/critique/${jobId}`);
}

// ---------------------------------------------------------------------------
// Polling helper
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2_500;
const POLL_TIMEOUT_MS = 180_000; // 3 min

/** Poll until status is 'completed' or 'failed'; throws on timeout or failure. */
export async function pollCritique(
  jobId: string,
  onPoll?: (data: CritiqueJobOut) => void,
): Promise<CritiqueJobOut> {
  const start = Date.now();

  while (Date.now() - start < POLL_TIMEOUT_MS) {
    const response = await getCritique(jobId);

    if (!response.data) {
      throw new Error(response.error?.message ?? 'Empty response from server');
    }

    const data = response.data;
    onPoll?.(data);

    if (data.status === 'completed') return data;
    if (data.status === 'failed') {
      throw new Error(data.error ?? 'Critique failed without error detail');
    }

    await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  throw new Error(`Critique timed out after ${POLL_TIMEOUT_MS / 1000}s`);
}
